import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { navigation, companyInfo } from '../../data.js';
import { cn } from '../../lib/utils.js';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      className={cn(
        'fixed top-0 z-50 w-full transition-all duration-300 bg-white py-2 shadow-sm'
      )}
    >
      <div className="container mx-auto px-4 md:px-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <Link to="/" className="flex items-center">
              <img 
                src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/0a7fa8d9-f148-4859-9cae-c4eced70be1a.gif" 
                alt="Sydthy Taxi Logo" 
                className="h-[50px] md:h-[70px] w-auto transition-all hover:scale-105"
              />
            </Link>
            <div className="h-8 w-[1px] bg-slate-200" />
            <Link to="/morsoe-turistfart" className="flex items-center">
              <img 
                src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/30278c8e-bcc6-45e6-9dc8-78f5cbf2c291.jpg" 
                alt="Morsø Turistfart Logo" 
                className="h-[50px] md:h-[70px] w-auto transition-all hover:scale-105 object-contain"
              />
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden items-center gap-8 min-[1200px]:flex">
            {navigation.map((item) => (
              <div key={item.name} className="relative group">
                <Link
                  to={item.href}
                  className={cn(
                    'text-sm font-medium transition-colors hover:text-blue-600 py-4 flex items-center gap-1',
                    location.pathname === item.href || (item.subItems && location.pathname.startsWith(item.href)) ? 'text-blue-600' : 'text-slate-600'
                  )}
                >
                  {item.name}
                  {item.subItems && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                  )}
                </Link>
                
                {item.subItems && (
                  <div className="absolute left-0 top-full hidden w-48 flex-col bg-white shadow-lg border border-slate-100 group-hover:flex">
                    {item.subItems.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.href}
                        className={cn(
                          'px-4 py-3 text-sm transition-colors hover:bg-slate-50 hover:text-blue-600',
                          location.pathname === subItem.href ? 'text-blue-600 bg-slate-50' : 'text-slate-600'
                        )}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a
              href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
              className={cn(
                'flex items-center gap-2 rounded-none px-6 py-2.5 text-sm font-semibold transition-all bg-slate-900 text-white hover:bg-slate-800'
              )}
            >
              {companyInfo.phone}
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            className={cn(
              'min-[1200px]:hidden text-slate-900'
            )}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute inset-x-0 top-full h-screen bg-white p-6 min-[1200px]:hidden"
          >
            <div className="flex flex-col gap-6">
              {navigation.map((item) => (
                <div key={item.name} className="flex flex-col gap-3">
                  <Link
                    to={item.href}
                    className={cn(
                      "text-lg font-medium transition-colors hover:text-blue-600",
                      location.pathname === item.href || (item.subItems && location.pathname.startsWith(item.href)) ? "text-blue-600" : "text-slate-900"
                    )}
                  >
                    {item.name}
                  </Link>
                  {item.subItems && (
                    <div className="flex flex-col gap-3 pl-4 border-l-2 border-slate-100">
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className={cn(
                            "text-base font-medium transition-colors hover:text-blue-600",
                            location.pathname === subItem.href ? "text-blue-600" : "text-slate-600"
                          )}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <hr className="border-slate-100" />
              <div className="flex flex-col gap-4">
                <p className="text-sm font-semibold uppercase tracking-wider text-slate-400">Kontakt os</p>
                <a
                  href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 text-xl font-bold text-slate-900"
                >
                  <Phone className="h-5 w-5 text-blue-600" />
                  {companyInfo.phone}
                </a>
                <p className="text-slate-600">{companyInfo.email}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
